import { FUNNELS, LOCALE_CODES } from './config';
import type { FunnelId } from './config';

const FUNNEL_PREFIXES: string[] = [...FUNNELS];
const LOCALES = new Set(LOCALE_CODES);
const DEFAULT_LOCALE = 'en';

export type FunnelRouteResolution
  = | { redirect: string, funnel?: never }
    | { redirect?: never, funnel: FunnelId };

export function funnelFromPath(path: string): FunnelId {
  const segment = path.split('/')[1] ?? '';

  return FUNNEL_PREFIXES.includes(segment) ? (segment as FunnelId) : 'baseline';
}

export function isLegacyFunnelApiPath(path: string): boolean {
  const funnel = funnelFromPath(path);

  return funnel !== 'baseline' && path.startsWith(`/${funnel}/api/`);
}

export function resolveFunnelRoute(rawPath: string): FunnelRouteResolution {
  const queryIndex = rawPath.indexOf('?');
  const path = queryIndex === -1 ? rawPath : rawPath.slice(0, queryIndex);
  const search = queryIndex === -1 ? '' : rawPath.slice(queryIndex);
  const redirectTo = (target: string): FunnelRouteResolution => ({ redirect: `${target}${search}` });

  const segments = path.split('/');
  if (segments.length >= 3 && LOCALES.has(segments[1]) && FUNNEL_PREFIXES.includes(segments[2])) {
    const [, locale, funnel, ...rest] = segments;
    const tail = rest.filter(Boolean).length ? `/${rest.filter(Boolean).join('/')}` : '';
    return redirectTo(`/${funnel}/${locale}${tail}`);
  }

  for (const funnel of FUNNEL_PREFIXES) {
    const prefix = `/${funnel}`;
    if (path === prefix || path === `${prefix}/`) {
      return redirectTo(`${prefix}/${DEFAULT_LOCALE}`);
    }
    if (path.startsWith(`${prefix}/`)) {
      return { funnel: funnel as FunnelId };
    }
  }

  if (path === '' || path === '/') {
    return redirectTo(`/${DEFAULT_LOCALE}`);
  }

  return { funnel: 'baseline' };
}

export function resolveMissingLocalePath(path: string, funnel: FunnelId): string | null {
  if (funnel === 'baseline') return null;

  const prefix = `/${funnel}`;
  if (path === prefix) return `${prefix}/${DEFAULT_LOCALE}`;
  if (!path.startsWith(`${prefix}/`)) return null;

  const rest = path.slice(prefix.length);
  const firstSegment = rest.split('/')[1] ?? '';
  if (LOCALES.has(firstSegment)) return null;

  return `${prefix}/${DEFAULT_LOCALE}${rest}`;
}
